import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Copy, Download, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
export const STATUSES = ['New', 'Invited to test', 'Joined', 'Not relevant'];

// Obvious test rows: example.com / test.com / *.test / icraftz-test.com, prod-diag, a "test." local part, or no name.
export const isTestRow = (e) => {
  const email = (e.email || '').toLowerCase();
  return /@(example|test|icraftz-test)\.com$/.test(email) || /\.test$/.test(email) || /^test[.\-_+]|\.test\./.test(email)
    || email.includes('prod-diag') || !(e.name || '').trim();
};

const COLS = ['email', 'name', 'admin_status', 'whatsapp', 'language', 'age_group', 'interests', 'willing_to_pay', 'content_preference', 'first_book', 'consent', 'timestamp', 'source'];
const csvCell = (v) => {
  let s = Array.isArray(v) ? v.join('; ') : v == null ? '' : String(v);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const copyText = async (text) => {
  try { await navigator.clipboard.writeText(text); return true; } catch (e) {
    const ta = Object.assign(document.createElement('textarea'), { value: text });
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy'); ta.remove(); return ok;
  }
};

export function WaitlistPanel({ entries, setEntries, loading, error, token, formatDate }) {
  const [search, setSearch] = useState('');
  const [realOnly, setRealOnly] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [flash, setFlash] = useState('');
  const [saveError, setSaveError] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const rows = useMemo(() => entries.filter((e) => {
    const status = e.admin_status || 'New';
    if (realOnly && (isTestRow(e) || status === 'Not relevant')) return false;
    if (statusFilter && status !== statusFilter) return false;
    return !search || (e.email || '').toLowerCase().includes(search.toLowerCase());
  }), [entries, realOnly, statusFilter, search]);

  const note = (msg) => { setFlash(msg); setTimeout(() => setFlash(''), 2500); };
  const emails = [...new Set(rows.map((r) => r.email).filter(Boolean))];

  const shownSelected = emails.filter((e) => selected.has(e));
  const toggle = (email) => setSelected((s) => { const n = new Set(s); n.has(email) ? n.delete(email) : n.add(email); return n; });
  const allOn = emails.length > 0 && shownSelected.length === emails.length;
  const toggleAll = () => setSelected(allOn ? new Set() : new Set(emails));
  const bulkInvite = async () => {
    setBulkBusy(true); setSaveError('');
    try {
      const r = await axios.patch(`${API}/api/admin/waitlist/status/bulk`, { emails: shownSelected, status: 'Invited to test' }, { headers: { Authorization: `Bearer ${token}` } });
      const set = new Set(shownSelected);
      setEntries(entries.map((e) => (set.has(e.email) ? { ...e, admin_status: 'Invited to test' } : e)));
      setSelected(new Set());
      note(`Marked ${r.data.matched} as Invited to test`);
    } catch (e) {
      setSaveError(e.response?.data?.detail || 'Could not update statuses.');
    } finally {
      setBulkBusy(false);
    }
  };

  const copyEmails = async () => note((await copyText(emails.join(', '))) ? `Copied ${emails.length} email${emails.length === 1 ? '' : 's'}` : 'Copy failed');
  const exportCsv = () => {
    const body = [COLS.join(','), ...rows.map((r) => COLS.map((c) => csvCell(c === 'admin_status' ? r.admin_status || 'New' : r[c])).join(','))].join('\n');
    const url = URL.createObjectURL(new Blob([body], { type: 'text/csv;charset=utf-8' }));
    Object.assign(document.createElement('a'), { href: url, download: `icraftz_waitlist_filtered_${new Date().toISOString().slice(0, 10)}.csv` }).click();
    URL.revokeObjectURL(url);
  };
  const setStatus = async (email, status) => {
    setSaveError('');
    const prev = entries;
    setEntries(entries.map((e) => (e.email === email ? { ...e, admin_status: status } : e)));
    try {
      await axios.patch(`${API}/api/admin/waitlist/status`, { email, status }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) {
      setEntries(prev);
      setSaveError(e.response?.data?.detail || 'Could not save status.');
    }
  };

  return (
    <>
      <section className="admin-stats">
        <div className="admin-stat-card"><p className="admin-stat-label">Total Signups</p><p className="admin-stat-value" data-testid="admin-total-count">{entries.length}</p></div>
        <div className="admin-stat-card"><p className="admin-stat-label">Shown</p><p className="admin-stat-value" data-testid="admin-waitlist-shown-count">{rows.length}</p></div>
        <div className="admin-stat-card"><p className="admin-stat-label">Latest Signup</p><p className="admin-stat-value admin-stat-value-sm">{entries.length > 0 ? formatDate(entries[0].timestamp) : '—'}</p></div>
      </section>

      <section className="admin-table-section">
        <div className="admin-table-controls admin-waitlist-controls">
          <input type="text" placeholder="Search by email..." value={search} onChange={(e) => setSearch(e.target.value)} className="admin-search-input" data-testid="admin-search-input" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="admin-search-input admin-select" data-testid="admin-waitlist-status-filter">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <label className="admin-check" data-testid="admin-waitlist-real-only-label">
            <input type="checkbox" checked={realOnly} onChange={(e) => setRealOnly(e.target.checked)} data-testid="admin-waitlist-real-only" /> Real entries only
          </label>
          <button type="button" className="admin-action-btn admin-refresh-btn" onClick={bulkInvite} disabled={!shownSelected.length || bulkBusy} data-testid="admin-waitlist-bulk-invite">
            {bulkBusy ? <Loader2 className="admin-spinner" size={14} /> : `Mark ${shownSelected.length || ''} selected as Invited to test`}
          </button>
          <button type="button" className="admin-action-btn admin-refresh-btn" onClick={copyEmails} disabled={!emails.length} data-testid="admin-waitlist-copy-emails"><Copy size={14} /> Copy emails</button>
          <button type="button" className="admin-action-btn admin-export-btn" onClick={exportCsv} disabled={!rows.length} data-testid="admin-waitlist-export-filtered"><Download size={14} /> Export CSV of filtered rows</button>
          {flash && <span className="admin-flash" role="status" data-testid="admin-waitlist-flash">{flash}</span>}
        </div>
        {(error || saveError) && <p className="admin-error" data-testid="admin-waitlist-error">{error || saveError}</p>}
        {loading ? (
          <div className="admin-table-loading"><Loader2 className="admin-spinner" size={24} /></div>
        ) : rows.length === 0 ? (
          <p className="admin-empty">{entries.length === 0 ? 'No signups yet.' : 'No matching entries.'}</p>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table admin-table-wide" data-testid="admin-waitlist-table">
              <thead><tr><th><input type="checkbox" checked={allOn} onChange={toggleAll} aria-label="Select all shown" data-testid="admin-waitlist-select-all" /></th><th>#</th><th>Email</th><th>Name</th><th>Status</th><th>WhatsApp</th><th>Lang</th><th>Age</th><th>Interests</th><th>Pay?</th><th>Format</th><th>First Book</th><th>Consent</th><th>Signed up</th></tr></thead>
              <tbody>
                {rows.map((entry, idx) => (
                  <tr key={`${entry.email}-${idx}`} data-testid={`admin-waitlist-row-${idx}`}>
                    <td><input type="checkbox" checked={selected.has(entry.email)} onChange={() => toggle(entry.email)} aria-label={`Select ${entry.email}`} data-testid={`admin-waitlist-select-${idx}`} /></td>
                    <td className="admin-td-num">{idx + 1}</td>
                    <td className="admin-td-email">{entry.email}</td>
                    <td>{entry.name || '—'}</td>
                    <td>
                      <select value={entry.admin_status || 'New'} onChange={(e) => setStatus(entry.email, e.target.value)} className="admin-select admin-status-select" data-testid={`admin-waitlist-status-${idx}`}>
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="admin-td-mono">{entry.whatsapp || '—'}</td>
                    <td>{entry.language || '—'}</td>
                    <td>{entry.age_group || '—'}</td>
                    <td className="admin-td-tags">{Array.isArray(entry.interests) && entry.interests.length > 0 ? entry.interests.join(', ') : '—'}</td>
                    <td>{entry.willing_to_pay || '—'}</td>
                    <td>{entry.content_preference || '—'}</td>
                    <td>{entry.first_book || '—'}</td>
                    <td className="admin-td-consent">{entry.consent === true ? '✓' : entry.consent === false ? '✗' : '—'}</td>
                    <td className="admin-td-date">{formatDate(entry.timestamp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
